import React, { Fragment, useContext, useRef, useState, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import translations from "../../../common/Translations";
import AppContext from "../../../../context/AppContext";
import CommonButton from "../../../common/CommonButton";
import datePickerPopper from "../../../common/DatePickerPopper";
import ModalCloseButton from "../../../common/ModalCloseButton";

const parseYmd = (s) => {
  if (!s) return null;
  const p = String(s).split("-").map(Number);
  if (p.length < 3 || p.some((n) => Number.isNaN(n))) return null;
  return new Date(p[0], p[1] - 1, p[2]);
};
const formatYmd = (d) => {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const DateFilter = ({
  dateFilter,
  setDateFilter,
  toDate,
  setToDate,
  fromDate,
  setFromDate,
}) => {
  const cancelButtonRef = useRef(null);
  const { theme, langMode } = useContext(AppContext);
  const [tempFromDate, setTempFromDate] = useState(fromDate);
  const [tempToDate, setTempToDate] = useState(toDate);
  const todayPicker = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const handleApply = () => {
    if (tempFromDate) {
      setFromDate(tempFromDate);
    }
    if (tempToDate) {
      setToDate(tempToDate);
    }
    setDateFilter(false);
  };

  return (
    <Transition.Root show={dateFilter} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={() => setDateFilter(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/45 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                data-tooltip-bounds
                className={`relative w-full max-w-md transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all ${theme === "dark"
                    ? "dark-mode"
                    : theme === "high-contrast"
                      ? "high-contrast"
                      : ""
                  }`}
              >
                <div
                  className={`rounded-xl p-6 ${theme === "dark"
                      ? "dark-mode"
                      : theme === "high-contrast"
                        ? "high-contrast"
                        : ""
                    }`}
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h3 className="min-w-0 text-sm font-semibold text-gray-900 dark:text-white">
                      {translations[langMode].datefilterforchart}
                    </h3>
                    <ModalCloseButton onClick={() => setDateFilter(false)} />
                  </div>
                  <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
                    <div className="flex flex-col gap-2 min-w-0">
                      <label className="text-sm font-medium">
                        {translations[langMode]?.fromdate || "From Date"}
                      </label>
                      <DatePicker
                        selected={parseYmd(tempFromDate || fromDate)}
                        onChange={(date) =>
                          setTempFromDate(date ? formatYmd(date) : "")
                        }
                        placeholderText={translations[langMode]?.fromdate || "From Date"}
                        maxDate={todayPicker}
                        selectsStart
                        startDate={parseYmd(tempFromDate || fromDate)}
                        endDate={parseYmd(tempToDate || toDate)}
                        wrapperClassName="w-full"
                        className={`w-full h-9 px-2 !rounded-lg text-sm border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""}`}
                        calendarClassName="!rounded-lg shadow-lg"
                        popperPlacement="bottom-start"
                        popperContainer={datePickerPopper}
                      />
                    </div>
                    <div className="flex flex-col gap-2 min-w-0">
                      <label className="text-sm font-medium">
                        {translations[langMode]?.todate || "To Date"}
                      </label>
                      <DatePicker
                        selected={parseYmd(tempToDate || toDate)}
                        onChange={(date) =>
                          setTempToDate(date ? formatYmd(date) : "")
                        }
                        placeholderText={translations[langMode]?.todate || "To Date"}
                        minDate={parseYmd(tempFromDate || fromDate)}
                        maxDate={todayPicker}
                        selectsEnd
                        startDate={parseYmd(tempFromDate || fromDate)}
                        endDate={parseYmd(tempToDate || toDate)}
                        disabled={!(tempFromDate || fromDate)}
                        wrapperClassName="w-full"
                        className={`w-full h-9 px-2 !rounded-lg text-sm border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""}`}
                        calendarClassName="!rounded-lg shadow-lg"
                        popperPlacement="bottom-start"
                        popperContainer={datePickerPopper}
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end gap-3">
                    <CommonButton colorIntent="danger"
                      onClick={() => setDateFilter(false)}
                      variant="danger"
                      tooltipText={translations[langMode].cancelandclose}
                      tooltipPosition="top"
                    >
                      {translations[langMode].cancel}
                    </CommonButton>
                    <CommonButton colorIntent="primary"
                      onClick={handleApply}
                      variant="primary"
                      tooltipText={translations[langMode].applytheselecteddaterange}
                      tooltipPosition="top"
                    >
                      {translations[langMode].apply}
                    </CommonButton>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
